import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Printer, Mail, CreditCard, X, AlertCircle, CheckCircle2, Download } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };
const SB = { draft: "bg-[#1A1B1A]/5 text-[#757675]", issued: "bg-blue-50 text-blue-600", unpaid: "bg-amber-50 text-amber-600", paid: "bg-[#205B5A]/10 text-[#205B5A]", "partially-paid": "bg-amber-50 text-amber-600", cancelled: "bg-red-50 text-red-400" };
const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-notes-preview/artifacts/qfix72o2_Lumero_Logo_transparent_with_name-removebg-preview.png";

/* Print styles for the invoice document only - mounted with the page so they never affect other routes */
const INVOICE_PRINT_CSS = `
    @media print {
      @page { margin: 20mm 15mm; }
      body.lf-invoice-print * { visibility: hidden !important; }
      body.lf-invoice-print [data-testid="invoice-document"], body.lf-invoice-print [data-testid="invoice-document"] * { visibility: visible !important; }
      body.lf-invoice-print [data-testid="invoice-document"] { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; border: none !important; border-radius: 0 !important; box-shadow: none !important; }
      body.lf-invoice-print .print\\:hidden, body.lf-invoice-print nav, body.lf-invoice-print aside, body.lf-invoice-print header, body.lf-invoice-print footer, body.lf-invoice-print #emergent-badge, body.lf-invoice-print [data-testid="sidebar"], body.lf-invoice-print [data-testid="topbar"] { display: none !important; }
    }
  `;

function useInvoicePrintStyles() {
  useEffect(() => {
    let style = document.getElementById("lf-print-styles");
    if (!style) {
      style = document.createElement("style");
      style.id = "lf-print-styles";
      style.textContent = INVOICE_PRINT_CSS;
      document.head.appendChild(style);
    }
    document.body.classList.add("lf-invoice-print");
    return () => document.body.classList.remove("lf-invoice-print");
  }, []);
}

export default function InvoiceDetailPage() {
  useInvoicePrintStyles();
  const { id } = useParams();
  const { clinic } = useAuth();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [payForm, setPayForm] = useState({ paid_amount: 0, payment_method: "card" });
  const [showEmail, setShowEmail] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [emailMsg, setEmailMsg] = useState("Here is your invoice. You can view, print or save it using the secure link below. If you have any questions, please contact the clinic.");
  const printRef = useRef(null);

  const load = () => api.get(`/invoices/${id}`).then(r => setInv(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  const updateStatus = async (status) => { await api.put(`/invoices/${id}`, { status }); load(); };

  const recordPayment = async () => {
    const amount = payForm.paid_amount || inv.total;
    const newStatus = amount >= inv.total ? "paid" : "partially-paid";
    await api.put(`/invoices/${id}`, { status: newStatus, paid_amount: amount, payment_method: payForm.payment_method, paid_date: new Date().toISOString().split("T")[0] });
    setShowPayment(false);
    load();
  };

  const handlePrint = () => { window.print(); };

  const handleDownloadPDF = async () => {
    const { generateInvoicePdf } = await import("@/lib/pdfSystem");
    await generateInvoicePdf(inv);
  };

  const handleEmail = async () => {
    setSendingEmail(true);
    setEmailResult(null);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/email`, { message: emailMsg || null });
      const ok = data.email_status === "sent" || data.email_status === "mock";
      setEmailResult({ ok, message: data.message });
      if (ok) {
        setShowEmail(false);
        load();
      }
    } catch (e) {
      setEmailResult({ ok: false, message: e.response?.data?.detail || "Email could not be sent. Please try again or download the PDF and send it manually." });
    }
    setSendingEmail(false);
  };

  if (loading) return <div className="p-6 md:p-8"><div className="animate-pulse h-8 bg-[#1A1B1A]/[0.04] rounded-xl w-48" /></div>;
  if (!inv) return <div className="p-6 md:p-8 text-sm text-[#757675]">Invoice not found</div>;

  const c = inv.clinic || {};
  const p = inv.patient || {};
  const outstanding = inv.total - (inv.paid_amount || 0);

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="invoice-detail-page">
      {/* Navigation + Actions bar */}
      <div className="flex items-center justify-between mb-5 print:hidden">
        <Link to="/app/invoices" className="text-sm text-[#757675] hover:text-[#1A1B1A] flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Invoices</Link>
        <div className="flex items-center gap-2">
          <button onClick={handleDownloadPDF} data-testid="download-pdf-btn"
            className="text-[11px] font-medium text-[#4A4B4A] border border-[#1A1B1A]/15 px-3.5 py-1.5 rounded-full hover:bg-[#FAF9F6] flex items-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> Download PDF
          </button>
          <button onClick={handlePrint} data-testid="print-invoice-btn"
            className="text-[11px] font-medium text-[#4A4B4A] border border-[#1A1B1A]/15 px-3.5 py-1.5 rounded-full hover:bg-[#FAF9F6] flex items-center gap-1.5">
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
          <button onClick={() => setShowEmail(true)} data-testid="email-invoice-btn"
            className="text-[11px] font-medium text-[#4A4B4A] border border-[#1A1B1A]/15 px-3.5 py-1.5 rounded-full hover:bg-[#FAF9F6] flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" /> Email
          </button>
          {(inv.status === "draft" || inv.status === "issued" || inv.status === "unpaid") && (
            <button onClick={() => { setPayForm({ paid_amount: outstanding, payment_method: "card" }); setShowPayment(true); }} data-testid="record-payment-btn"
              className="text-[11px] font-medium text-white bg-[#205B5A] px-3.5 py-1.5 rounded-full hover:bg-[#174544] flex items-center gap-1.5">
              <CreditCard className="w-3.5 h-3.5" /> Record payment
            </button>
          )}
        </div>
      </div>

      {/* Status bar */}
      <div className="flex items-center gap-3 mb-5 print:hidden">
        <span className={`text-[10px] font-semibold px-3 py-1 rounded-full capitalize ${SB[inv.status] || SB.draft}`}>{inv.status}</span>
        {inv.status === "draft" && <button onClick={() => updateStatus("issued")} className="text-[10px] font-medium text-blue-600 hover:text-blue-700">Mark as issued</button>}
        {inv.status === "issued" && <button onClick={() => updateStatus("unpaid")} className="text-[10px] font-medium text-amber-600">Mark unpaid</button>}
        {inv.status !== "cancelled" && inv.status !== "paid" && <button onClick={() => updateStatus("cancelled")} className="text-[10px] font-medium text-red-400 hover:text-red-500 ml-auto">Cancel invoice</button>}
      </div>

      {/* Professional Invoice Document */}
      <div ref={printRef} className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden print:border-none print:rounded-none print:shadow-none" data-testid="invoice-document">
        {/* Header */}
        <div className="p-8 pb-6 flex items-start justify-between border-b border-[#1A1B1A]/[0.06]">
          <div>
            {c.logo_url ? (
              <img src={c.logo_url} alt={c.name} className="h-10 w-auto mb-3" />
            ) : (
              <p className="text-xl font-semibold text-[#1A1B1A] mb-3" style={H}>{c.name}</p>
            )}
            {c.address && <p className="text-xs text-[#757675]">{c.address}</p>}
            {c.phone && <p className="text-xs text-[#757675]">{c.phone}</p>}
            {c.email && <p className="text-xs text-[#757675]">{c.email}</p>}
            {c.website && <p className="text-xs text-[#757675]">{c.website}</p>}
          </div>
          <div className="text-right">
            <h1 className="text-2xl font-medium text-[#1A1B1A] mb-2" style={H}>INVOICE</h1>
            <p className="text-sm font-semibold text-[#1A1B1A]">{inv.invoice_number}</p>
            <div className="mt-2 space-y-0.5">
              <p className="text-[10px] text-[#757675]">Issue date: <span className="text-[#1A1B1A] font-medium">{inv.issue_date || inv.created_at?.split("T")[0]}</span></p>
              {inv.due_date && <p className="text-[10px] text-[#757675]">Due date: <span className="text-[#1A1B1A] font-medium">{inv.due_date}</span></p>}
            </div>
          </div>
        </div>

        {/* Bill To */}
        <div className="px-8 py-5 flex justify-between border-b border-[#1A1B1A]/[0.06]">
          <div>
            <p className="text-[9px] font-semibold text-[#757675] uppercase tracking-wider mb-1">Bill to</p>
            <p className="text-sm font-medium text-[#1A1B1A]">{p.title ? `${p.title} ` : ""}{p.first_name} {p.last_name}</p>
            {p.email && <p className="text-xs text-[#757675]">{p.email}</p>}
            {p.phone && <p className="text-xs text-[#757675]">{p.phone}</p>}
            {p.address && <p className="text-xs text-[#757675]">{p.address}</p>}
          </div>
          {inv.practitioner_name && (
            <div className="text-right">
              <p className="text-[9px] font-semibold text-[#757675] uppercase tracking-wider mb-1">Practitioner</p>
              <p className="text-sm text-[#1A1B1A]">{inv.practitioner_name}</p>
            </div>
          )}
        </div>

        {/* Items table */}
        <div className="px-8 py-5">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-[#1A1B1A]/[0.08]">
                <th className="text-left py-2.5 text-[10px] font-semibold text-[#757675] uppercase tracking-wider">Description</th>
                <th className="text-center py-2.5 text-[10px] font-semibold text-[#757675] uppercase tracking-wider w-16">Qty</th>
                <th className="text-right py-2.5 text-[10px] font-semibold text-[#757675] uppercase tracking-wider w-24">Unit price</th>
                <th className="text-right py-2.5 text-[10px] font-semibold text-[#757675] uppercase tracking-wider w-24">Amount</th>
              </tr>
            </thead>
            <tbody>
              {inv.items?.map((item, i) => (
                <tr key={i} className="border-b border-[#1A1B1A]/[0.04]">
                  <td className="py-3 text-sm text-[#1A1B1A]">{item.description}</td>
                  <td className="py-3 text-sm text-[#757675] text-center">{item.quantity}</td>
                  <td className="py-3 text-sm text-[#757675] text-right">£{item.unit_price?.toFixed(2)}</td>
                  <td className="py-3 text-sm font-medium text-[#1A1B1A] text-right">£{item.total?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mt-4">
            <div className="w-56 space-y-1.5">
              <div className="flex justify-between text-sm"><span className="text-[#757675]">Subtotal</span><span className="text-[#1A1B1A]">£{inv.subtotal?.toFixed(2)}</span></div>
              {inv.tax > 0 && <div className="flex justify-between text-sm"><span className="text-[#757675]">Tax / VAT</span><span className="text-[#1A1B1A]">£{inv.tax?.toFixed(2)}</span></div>}
              <div className="flex justify-between text-base font-semibold border-t-2 border-[#1A1B1A]/[0.08] pt-2"><span className="text-[#1A1B1A]">Total</span><span className="text-[#1A1B1A]">£{inv.total?.toFixed(2)}</span></div>
              {inv.paid_amount > 0 && (
                <>
                  <div className="flex justify-between text-sm text-[#205B5A]"><span>Paid</span><span>-£{inv.paid_amount?.toFixed(2)}</span></div>
                  <div className="flex justify-between text-sm font-semibold"><span className="text-[#1A1B1A]">Outstanding</span><span className={outstanding > 0 ? "text-amber-600" : "text-[#205B5A]"}>£{outstanding.toFixed(2)}</span></div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Payment info */}
        {inv.paid_amount > 0 && (
          <div className="px-8 py-4 border-t border-[#1A1B1A]/[0.06] bg-[#FAF9F6]">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#205B5A]" />
              <p className="text-xs font-medium text-[#205B5A]">Payment received</p>
            </div>
            <div className="mt-1 flex gap-4">
              <p className="text-[10px] text-[#757675]">Amount: <span className="font-medium text-[#1A1B1A]">£{inv.paid_amount?.toFixed(2)}</span></p>
              {inv.payment_method && <p className="text-[10px] text-[#757675]">Method: <span className="font-medium text-[#1A1B1A] capitalize">{inv.payment_method}</span></p>}
              {inv.paid_date && <p className="text-[10px] text-[#757675]">Date: <span className="font-medium text-[#1A1B1A]">{inv.paid_date}</span></p>}
            </div>
          </div>
        )}

        {/* Notes */}
        {inv.notes && (
          <div className="px-8 py-4 border-t border-[#1A1B1A]/[0.06]">
            <p className="text-[9px] font-semibold text-[#757675] uppercase tracking-wider mb-1">Notes</p>
            <p className="text-xs text-[#4A4B4A]">{inv.notes}</p>
          </div>
        )}

        {/* Footer */}
        <div className="px-8 py-4 border-t border-[#1A1B1A]/[0.06] bg-[#FAF9F6]">
          <p className="text-[10px] text-[#757675] text-center">Thank you for choosing {c.name}.</p>
        </div>
      </div>

      {/* Record Payment Modal */}
      {showPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 print:hidden" data-testid="payment-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-medium text-[#1A1B1A] mb-4" style={H}>Record payment</h2>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-[#757675] mb-1">Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#757675]">£</span>
                  <input type="number" step="0.01" value={payForm.paid_amount} onChange={e => setPayForm({ ...payForm, paid_amount: Number(e.target.value) })} data-testid="payment-amount"
                    className="w-full pl-7 pr-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#757675] mb-1">Method</label>
                <select value={payForm.payment_method} onChange={e => setPayForm({ ...payForm, payment_method: e.target.value })} data-testid="payment-method"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]">
                  <option value="card">Card</option><option value="cash">Cash</option><option value="bank-transfer">Bank transfer</option><option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowPayment(false)} className="px-4 py-2 text-sm text-[#757675]">Cancel</button>
              <button onClick={recordPayment} data-testid="confirm-payment-btn"
                className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544]">Confirm payment</button>
            </div>
          </div>
        </div>
      )}

      {emailResult && (
        <div className={`mb-4 rounded-xl px-4 py-3 flex items-start gap-2 print:hidden ${emailResult.ok ? "bg-[#205B5A]/10" : "bg-red-50 border border-red-200"}`}
          data-testid="invoice-email-feedback">
          {emailResult.ok
            ? <CheckCircle2 className="w-4 h-4 text-[#205B5A] mt-0.5 flex-shrink-0" />
            : <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
          <p className={`text-xs ${emailResult.ok ? "text-[#205B5A]" : "text-red-600"}`}>{emailResult.message}</p>
          <button onClick={() => setEmailResult(null)} className="ml-auto text-[10px] text-[#757675]">Dismiss</button>
        </div>
      )}

      {/* Email Modal */}
      {showEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 print:hidden" data-testid="email-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-medium text-[#1A1B1A] mb-1" style={H}>Email invoice</h2>
            <p className="text-xs text-[#757675] mb-3">Send {inv.invoice_number} to {p.email || "no email on file"}</p>
            <div className="bg-[#FAF9F6] rounded-xl px-3 py-2.5 mb-3 space-y-1" data-testid="email-invoice-summary">
              <p className="text-[11px] text-[#4A4B4A]">Invoice <span className="text-[#1A1B1A]">{inv.invoice_number}</span></p>
              <p className="text-[11px] text-[#4A4B4A]">Total <span className="text-[#1A1B1A]">£{Number(inv.total || 0).toFixed(2)}</span></p>
              <p className="text-[11px] text-[#4A4B4A]">Status <span className="text-[#1A1B1A]">{outstanding <= 0 ? "Paid in full" : `£${outstanding.toFixed(2)} outstanding`}</span></p>
              <p className="text-[10px] text-[#757675]">The patient gets a secure link to view, print or save the invoice. It expires in 30 days.</p>
            </div>
            {!p.email && (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3">
                <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-[10px] text-amber-700">No email address on file. Please update the patient record first.</span>
              </div>
            )}
            <div className="mb-4">
              <label className="block text-xs font-medium text-[#757675] mb-1">Message</label>
              <textarea value={emailMsg} onChange={e => setEmailMsg(e.target.value)} rows={3}
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowEmail(false)} className="px-4 py-2 text-sm text-[#757675]">Cancel</button>
              <button onClick={handleEmail} disabled={!p.email || sendingEmail} data-testid="send-email-btn"
                className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544] disabled:opacity-40 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> {sendingEmail ? "Sending..." : "Send invoice"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
